import axios from 'axios';
import { API_URL } from '../config';
import { getAuthToken, setAuthToken, removeAuthToken } from './authToken';
import { installOfflineInterceptor } from './offlineSync';
import { installReadCache, clearReadCache } from './readCache';
import { startOutbox, resumeAfterAuth } from './outbox';

// Token helpers live in authToken.js so the offline outbox can read the token
// when it drains without importing this module (which would be a cycle).
export { getAuthToken, setAuthToken, removeAuthToken };

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true
});

// Add auth token to all requests
api.interceptors.request.use(async (config) => {
  const token = await getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses - clear token and trigger re-auth
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response && error.response.status === 401) {
      await removeAuthToken();
    }
    return Promise.reject(error);
  }
);

// Offline support. Installed AFTER the 401 handler so a genuine auth failure is
// still cleared normally; the offline layer only ever sees requests that never
// reached the server. See offlineSync.js for what is and isn't queueable.
installOfflineInterceptor(api);
installReadCache(api);
startOutbox();

// ==================== CLIENT API ====================

export const getClients = () => {
  return api.get('/clients');
};

export const getClient = (id) => {
  return api.get(`/clients/${id}`);
};

export const createClient = (clientData) => {
  return api.post('/clients', clientData);
};

export const updateClient = (id, clientData) => {
  return api.put(`/clients/${id}`, clientData);
};

// Shareable client-hub portal link (lists all the client's projects)
export const getClientPortalLink = (id) => {
  return api.get(`/clients/${id}/portal-link`);
};

// ---- Client contact roster: the people at a company client (foreman, AP, PM). Distinct
// from job contacts, which are per-job. These persist across every job for the client.
export const getClientContacts = (clientId) => {
  return api.get(`/clients/${clientId}/contacts`);
};

export const addClientContact = (clientId, contact) => {
  return api.post(`/clients/${clientId}/contacts`, contact);
};

export const updateClientContact = (clientId, contactId, contact) => {
  return api.put(`/clients/${clientId}/contacts/${contactId}`, contact);
};

export const deleteClientContact = (clientId, contactId) => {
  return api.delete(`/clients/${clientId}/contacts/${contactId}`);
};

// Daily summary of jobs worked (notes + photos + hours) for a given YYYY-MM-DD date
export const getDailySummary = (dateStr) => {
  return api.get(`/daily-summary?date=${dateStr}`);
};

export const deleteClient = (id) => {
  return api.delete(`/clients/${id}`);
};

export const mergeClient = (sourceId, targetId) => {
  return api.post(`/clients/${sourceId}/merge`, { target_client_id: targetId });
};

export const getMergePreview = (sourceId, targetId) => {
  return api.get(`/clients/${sourceId}/merge-preview/${targetId}`);
};

// ==================== JOB API ====================

export const getJobs = (queryString = '') => {
  const url = queryString ? `/jobs?${queryString}` : '/jobs';
  return api.get(url);
};

// Lightweight job list for the Map page — only fields needed to plot pins (fast)
export const getJobMapPoints = () => {
  return api.get('/jobs/map-points');
};

export const getJob = (id) => {
  return api.get(`/jobs/${id}`);
};

export const createJob = (jobData) => {
  return api.post('/jobs', jobData);
};

export const updateJob = (id, jobData) => {
  return api.put(`/jobs/${id}`, jobData);
};

export const deleteJob = (id) => {
  return api.delete(`/jobs/${id}`);
};

export const getJobMergePreview = (sourceId, targetId) => {
  return api.get(`/jobs/${sourceId}/merge-preview/${targetId}`);
};

export const mergeJob = (sourceId, targetId) => {
  return api.post(`/jobs/${sourceId}/merge`, { target_job_id: targetId });
};

export const reassignTimeEntry = (entryId, jobId) => {
  return api.put(`/time-entries/${entryId}`, { job_id: jobId });
};

export const getSchedule = (startDate, endDate) => {
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  return api.get(`/schedule?${params.toString()}`);
};

export const scheduleJob = (jobId, data) => {
  return api.put(`/jobs/${jobId}/schedule`, data);
};

// ==================== QC CHECKLIST API ====================

export const listQCChecklists = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.status) qs.append('status', params.status);
  if (params.checklist_type) qs.append('checklist_type', params.checklist_type);
  return api.get(`/qc-checklists${qs.toString() ? '?' + qs.toString() : ''}`);
};

export const getQCChecklistStats = () => {
  return api.get('/qc-checklists/stats');
};

export const getQCChecklist = (id) => {
  return api.get(`/qc-checklists/${id}`);
};

export const createQCChecklist = (data) => {
  return api.post('/qc-checklists', data);
};

export const saveQCChecklist = (id, data) => {
  return api.put(`/qc-checklists/${id}/save`, data);
};

export const approveQCChecklist = (id) => {
  return api.post(`/qc-checklists/${id}/approve`);
};

export const deleteQCChecklist = (id) => {
  return api.delete(`/qc-checklists/${id}`);
};

export const getQCTemplates = () => {
  return api.get('/qc-checklists/templates');
};

// ==================== JOB COMMENTS API ====================

export const getJobComments = (jobId) => {
  return api.get(`/jobs/${jobId}/comments`);
};

export const addJobComment = (jobId, comment) => {
  return api.post(`/jobs/${jobId}/comments`, { comment });
};

export const deleteJobComment = (jobId, commentId) => {
  return api.delete(`/jobs/${jobId}/comments/${commentId}`);
};

// Jobs with field hours but no service type — invisible to every median until fixed.
export const getUnclassifiedJobs = () => api.get('/analytics/unclassified-jobs');
export const setJobServiceType = (jobId, serviceType, secondary = null) =>
  api.put(`/jobs/${jobId}/service-type`, { service_type: serviceType, service_type_secondary: secondary });

// ---- Crews ----
export const getCrews = (params) => api.get('/crews', { params });
export const createCrew = (data) => api.post('/crews', data);
export const updateCrew = (crewId, data) => api.put(`/crews/${crewId}`, data);
export const addCrewMember = (crewId, data) => api.post(`/crews/${crewId}/members`, data);
export const removeCrewMember = (crewId, memberId) => api.delete(`/crews/${crewId}/members/${memberId}`);
// Promote to Chief / step back to Member. Goes through the server so Crew.chief_id and
// CrewMember.role can never drift apart.
export const setCrewMemberRole = (crewId, memberId, role) =>
  api.put(`/crews/${crewId}/members/${memberId}/role`, { role });
// Who is doing this job — a crew, or one person working alone. This is what puts it
// in a column on the board. The two are mutually exclusive server-side.
export const assignJobCrew = (jobId, crewId, soloMemberId = null) =>
  api.put(`/jobs/${jobId}/crew`, { crew_id: crewId, solo_member_id: soloMemberId });

// ---- Data collectors ----
// The tablets report their own position so the board can cross-check where a crew is.
// Read-only observation: nothing here can start, stop or edit a timer.
export const getDevices = () => api.get('/devices');
// Returns the device_key. That is the ONLY response that ever carries it — it goes
// into the beacon once, and a lost key is rotated rather than looked up.
export const createDevice = (data) => api.post('/devices', data);
export const updateDevice = (deviceId, data) => api.put(`/devices/${deviceId}`, data);
export const rotateDeviceKey = (deviceId) => api.post(`/devices/${deviceId}/rotate-key`);
export const deleteDevice = (deviceId) => api.delete(`/devices/${deviceId}`);
// Arrivals and departures for a day — the raw material for comparing GPS against
// what the crews actually clocked.
export const getDeviceVisits = (date) => api.get('/devices/visits', { params: { date } });

// What the crew observed on site, captured once at field-work close-out.
// Queued like a job note when there's no signal — see offlineSync QUEUEABLE.
export const setJobFieldConditions = (jobId, payload) => {
  return api.post(`/jobs/${jobId}/field-conditions`, payload);
};

// Checklist items still marked unchecked across every visit to this job — the
// "what was left undone" view that sits above the comment thread.
export const getJobOpenItems = (jobId) => {
  return api.get(`/jobs/${jobId}/open-items`);
};

// ==================== JOB TEAM ASSIGNMENT API ====================

export const removeSelfFromJob = (jobId) => {
  return api.post(`/jobs/${jobId}/remove-self`);
};

export const selfAssignToJob = (jobId, memberId) => {
  return api.post(`/jobs/${jobId}/self-assign/${memberId}`);
};

// ==================== TEAM API ====================

// Defaults to active members only — 40+ callers (crew pickers, assignment dropdowns,
// schedule) depend on never being offered someone who has left. Only the Team admin
// page passes includeInactive, so departed staff stay visible there but nowhere else.
export const getTeamMembers = (includeInactive = false) => {
  return api.get(includeInactive ? '/team?include_inactive=1' : '/team');
};

export const getTeamMember = (id) => {
  return api.get(`/team/${id}`);
};

export const createTeamMember = (memberData) => {
  return api.post('/team', memberData);
};

export const updateTeamMember = (id, memberData) => {
  return api.put(`/team/${id}`, memberData);
};

export const deleteTeamMember = (id) => {
  return api.delete(`/team/${id}`);
};

// Offboarding is two calls on purpose: without confirm the server only REPORTS what it
// would change, so the UI can show the real job list before anything is touched.
export const previewOffboard = (id) => {
  return api.post(`/team/${id}/offboard`, {});
};

export const applyOffboard = (id) => {
  return api.post(`/team/${id}/offboard`, { confirm: true });
};

// ==================== TIME TRACKING API ====================

export const clockIn = (data) => {
  return api.post('/time-entries/clock-in', data);
};

export const clockOut = (entryId, data) => {
  return api.post(`/time-entries/${entryId}/clock-out`, data || {});
};

// Clock in self + helpers to one job as a crew group ({ job_id, team_member_ids, work_type, ... })
export const clockInCrew = (data) => {
  return api.post('/time-entries/clock-in-crew', data);
};

// Clock the whole crew out of a job
export const clockOutJob = (jobId, data) => {
  return api.post(`/time-entries/clock-out-job/${jobId}`, data || {});
};

// Work orders for a job (returns { work_orders, summary })
export const getWorkOrders = (jobId) => {
  return api.get(`/jobs/${jobId}/work-orders`);
};

// Split a completed entry across work orders ({ allocations: [{ work_order_id, hours }] })
export const splitTimeEntry = (entryId, allocations) => {
  return api.post(`/time-entries/${entryId}/split`, { allocations });
};

export const pauseTimeEntry = (entryId) => {
  return api.post(`/time-entries/${entryId}/pause`);
};

export const resumeTimeEntry = (entryId) => {
  return api.post(`/time-entries/${entryId}/resume`);
};

// Pause/resume the whole crew clocked in together on a job
export const pauseCrew = (jobId) => {
  return api.post(`/time-entries/pause-crew/${jobId}`);
};

export const resumeCrew = (jobId) => {
  return api.post(`/time-entries/resume-crew/${jobId}`);
};

export const getTimeEntries = () => {
  return api.get('/time-entries');
};

export const getActiveTimeEntries = () => {
  return api.get('/time-entries/active');
};

// All time entries for a job (job entries are visible to everyone) — used to show who's currently on the job
export const getJobTimeEntries = (jobId) => {
  return api.get('/time-entries', { params: { type: 'job', job_id: jobId } });
};

export const approveTimeEntry = (entryId, data) => {
  return api.post(`/time-entries/${entryId}/approve`, data);
};

export const startBreak = (entryId) => {
  return api.post(`/time-entries/${entryId}/start-break`);
};

export const endBreak = (entryId) => {
  return api.post(`/time-entries/${entryId}/end-break`);
};

// ==================== PAYROLL API ====================

export const getPayrollReport = (startDate, endDate) => {
  return api.get('/payroll/report', {
    params: { start_date: startDate, end_date: endDate }
  });
};

// ==================== DASHBOARD API ====================

export const getDashboardStats = () => {
  return api.get('/dashboard/stats');
};

// ==================== MAP API ====================

export const getMapJobs = () => {
  return api.get('/map/jobs');
};

export const uploadBoundaryFile = (jobId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/jobs/${jobId}/boundary`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const deleteBoundaryFile = (jobId) => {
  return api.delete(`/jobs/${jobId}/boundary`);
};

// ==================== AUTH API ====================

export const login = async (username, password) => {
  const response = await api.post('/auth/login', { username, password });
  // Store token for mobile auth
  if (response.data && response.data.token) {
    await setAuthToken(response.data.token);
    // A backend restart wipes every in-memory token, so crews get logged out mid-
    // day with work still queued. The outbox pauses rather than discarding it —
    // signing back in is what releases it.
    resumeAfterAuth();
  }
  return response;
};

export const logout = async () => {
  await removeAuthToken();
  // Drop cached reads so the next person to sign in on a shared truck tablet
  // can't page through the previous user's jobs. The outbox is deliberately NOT
  // cleared — unsynced work belongs to whoever made it and must survive.
  await clearReadCache();
  return api.post('/auth/logout');
};

export const getCurrentUser = () => {
  return api.get('/auth/me');
};

export const register = (userData) => {
  return api.post('/auth/register', userData);
};

// Parcel Import
export const getParcelFolders = (path = '') => {
  const params = path ? `?path=${encodeURIComponent(path)}` : '';
  return api.get(`/parcel-imports/folders${params}`);
};

export const getParcelProjects = (countyPath) => {
  return api.get(`/parcel-imports/projects?path=${encodeURIComponent(countyPath)}`);
};

export const previewParcelData = (projectPath) => {
  return api.get(`/parcel-imports/preview?path=${encodeURIComponent(projectPath)}`);
};

export const importParcelsToJob = (jobId, projectPath) => {
  return api.post(`/jobs/${jobId}/parcels/import`, { project_path: projectPath });
};

export const getJobParcels = (jobId) => {
  return api.get(`/jobs/${jobId}/parcels`);
};

export const deleteJobParcel = (jobId, parcelId) => {
  return api.delete(`/jobs/${jobId}/parcels/${parcelId}`);
};

export const clearJobParcels = (jobId) => {
  return api.delete(`/jobs/${jobId}/parcels/clear`);
};

export const uploadParcelsJson = (jobId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/jobs/${jobId}/parcels/upload-json`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// ==================== SUPPLY MANAGEMENT ====================
export const getSupplyCategories = () => api.get('/supplies/categories');
export const createSupplyCategory = (data) => api.post('/supplies/categories', data);
export const updateSupplyCategory = (id, data) => api.put(`/supplies/categories/${id}`, data);
export const deleteSupplyCategory = (id) => api.delete(`/supplies/categories/${id}`);
export const createSupplyAssignment = (catId, teamMemberId) => api.post(`/supplies/categories/${catId}/assignments`, { team_member_id: teamMemberId });
export const deleteSupplyAssignment = (id) => api.delete(`/supplies/assignments/${id}`);
export const getMySupplyChecks = () => api.get('/supplies/my-checks');
export const submitSupplyChecks = (checks) => api.post('/supplies/check', { checks });
export const getSupplyCheckHistory = (catId) => api.get(`/supplies/categories/${catId}/history`);
export const getSupplyOrders = () => api.get('/supplies/orders');
export const createSupplyOrder = (catId, data) => api.post(`/supplies/categories/${catId}/orders`, data);
export const updateSupplyOrder = (id, data) => api.put(`/supplies/orders/${id}`, data);
export const deleteSupplyOrder = (id) => api.delete(`/supplies/orders/${id}`);
export const upsertSupplier = (catId, data) => api.put(`/supplies/categories/${catId}/supplier`, data);

// ==================== EQUIPMENT & VEHICLES ====================
export const getEquipment = (type) => api.get(`/equipment${type ? `?type=${type}` : ''}`);
export const createEquipment = (data) => api.post('/equipment', data);
export const updateEquipment = (id, data) => api.put(`/equipment/${id}`, data);
export const deleteEquipment = (id) => api.delete(`/equipment/${id}`);
export const createEquipmentAssignment = (eqId, teamMemberId) => api.post(`/equipment/${eqId}/assignments`, { team_member_id: teamMemberId });
export const deleteEquipmentAssignment = (id) => api.delete(`/equipment/assignments/${id}`);
export const getEquipmentChecklist = (eqId) => api.get(`/equipment/${eqId}/checklist`);
export const createEquipmentChecklistItem = (eqId, data) => api.post(`/equipment/${eqId}/checklist`, data);
export const updateEquipmentChecklistItem = (id, data) => api.put(`/equipment/checklist/${id}`, data);
export const deleteEquipmentChecklistItem = (id) => api.delete(`/equipment/checklist/${id}`);
export const getMyEquipmentChecks = () => api.get('/equipment/my-checks');
export const submitEquipmentCheck = (eqId, data) => api.post(`/equipment/${eqId}/check`, data);
export const getEquipmentCheckHistory = (eqId) => api.get(`/equipment/${eqId}/history`);
export const getMaintenanceLogs = (eqId) => api.get(`/equipment/${eqId}/maintenance`);
export const createMaintenanceLog = (eqId, data) => api.post(`/equipment/${eqId}/maintenance`, data);
export const updateMaintenanceLog = (id, data) => api.put(`/equipment/maintenance/${id}`, data);
export const deleteMaintenanceLog = (id) => api.delete(`/equipment/maintenance/${id}`);

// ==================== PROFILE PHOTO ====================
export const uploadProfilePhoto = (memberId, file) => {
  const formData = new FormData();
  formData.append('photo', file);
  return api.post(`/team/${memberId}/profile-photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const deleteProfilePhoto = (memberId) => api.delete(`/team/${memberId}/profile-photo`);

// ==================== CHANGE ORDERS ====================
export const getChangeOrders = (jobId) => api.get(`/jobs/${jobId}/change-orders`);
export const createChangeOrder = (jobId, data) => api.post(`/jobs/${jobId}/change-orders`, data);
export const updateChangeOrder = (jobId, coId, data) => api.put(`/jobs/${jobId}/change-orders/${coId}`, data);
export const deleteChangeOrder = (jobId, coId) => api.delete(`/jobs/${jobId}/change-orders/${coId}`);
export const updateContractAmount = (jobId, amount) => api.put(`/jobs/${jobId}/contract-amount`, { contract_amount: amount });

// ==================== CAREER TRACKS ====================
export const getCareerTracks = (memberId) => api.get(`/career-tracks/${memberId}`);
export const assignCareerTrack = (data) => api.post('/career-tracks', data);
export const removeCareerTrack = (id) => api.delete(`/career-tracks/${id}`);

// ==================== CERTIFICATIONS ====================
export const getCertifications = (memberId) => api.get(`/certifications${memberId ? `?member_id=${memberId}` : ''}`);
export const createCertification = (data) => api.post('/certifications', data);
export const updateCertification = (id, data) => api.put(`/certifications/${id}`, data);
export const deleteCertification = (id) => api.delete(`/certifications/${id}`);
export const checkCertExpirations = () => api.post('/certifications/check-expirations');

// ==================== TOOLBOX TALKS ====================
export const getToolboxTalkTopics = (category) => api.get(`/toolbox-talks/topics${category ? `?category=${encodeURIComponent(category)}` : ''}`);
export const createToolboxTalkTopic = (data) => api.post('/toolbox-talks/topics', data);
export const updateToolboxTalkTopic = (id, data) => api.put(`/toolbox-talks/topics/${id}`, data);
export const deleteToolboxTalkTopic = (id) => api.delete(`/toolbox-talks/topics/${id}`);
export const getToolboxTalkSessions = (status) => api.get(`/toolbox-talks/sessions${status ? `?status=${status}` : ''}`);
export const createToolboxTalkSession = (data) => api.post('/toolbox-talks/sessions', data);
export const updateToolboxTalkSession = (id, data) => api.put(`/toolbox-talks/sessions/${id}`, data);
export const completeToolboxTalk = (id, data) => api.post(`/toolbox-talks/sessions/${id}/complete`, data);
export const deleteToolboxTalkSession = (id) => api.delete(`/toolbox-talks/sessions/${id}`);
export const checkToolboxTalkDue = () => api.get('/toolbox-talks/weekly-check');

// ==================== TRAINING / CST ====================
export const listTrainingModules = () => api.get('/training/modules');
export const getTrainingModule = (id) => api.get(`/training/modules/${id}`);
export const getTrainingLesson = (id) => api.get(`/training/lessons/${id}`);
export const submitPracticeQuiz = (lessonId, answers) =>
  api.post(`/training/lessons/${lessonId}/practice/submit`, { answers });
export const markLessonComplete = (lessonId) => api.post(`/training/lessons/${lessonId}/complete`);
export const requestLevelExam = (moduleId) => api.post(`/training/modules/${moduleId}/request-exam`);
export const listExamUnlocks = (status) =>
  api.get(`/training/exam-unlocks${status ? `?status=${status}` : ''}`);
export const issueUnlockCode = (unlockId) =>
  api.post(`/training/exam-unlocks/${unlockId}/issue-code`);
export const denyUnlock = (unlockId) => api.post(`/training/exam-unlocks/${unlockId}/deny`);
export const startLevelExam = (code) => api.post('/training/exam/start', { code });
export const saveExamAnswer = (attemptId, questionId, choice) =>
  api.post(`/training/exam-attempts/${attemptId}/save`, { question_id: questionId, choice });
export const submitExam = (attemptId, answers) =>
  api.post(`/training/exam-attempts/${attemptId}/submit`, { answers });
export const getExamAttempt = (attemptId) => api.get(`/training/exam-attempts/${attemptId}`);
export const approveAdvancement = (attemptId) =>
  api.post(`/training/exam-attempts/${attemptId}/approve-advancement`);

// Question answer-choice revisions (admin review queue)
export const getQuestionRevisions = (status = 'pending') => api.get(`/training/revisions?status=${status}`);
export const approveQuestionRevision = (id, data) => api.post(`/training/revisions/${id}/approve`, data || {});
export const rejectQuestionRevision = (id) => api.post(`/training/revisions/${id}/reject`);
export const bulkApproveRevisions = (ids) => api.post('/training/revisions/bulk-approve', { ids });
export const listPendingAdvancements = () => api.get('/training/pending-advancements');
export const getCareerRoster = () => api.get('/admin/career-roster');
export const updateCurrentTierHistory = (memberId, payload) =>
  api.patch(`/team/${memberId}/tier-history/current`, payload);
export const getMyCareerPath = () => api.get('/career/my-path');

// ==================== LEADERSHIP NOMINATIONS ====================

export const getNominationCategories = () => api.get('/nominations/categories');
export const getNominations = (params = {}) => api.get('/nominations', { params });
export const getNomination = (id) => api.get(`/nominations/${id}`);
export const createNomination = (data) => api.post('/nominations', data);
export const updateNomination = (id, data) => api.patch(`/nominations/${id}`, data);

// ==================== TODOS API ====================
export const getTodos = (params = {}) => api.get('/todos', { params });
export const createTodo = (data) => api.post('/todos', data);
export const updateTodo = (id, data) => api.put(`/todos/${id}`, data);
export const toggleTodoComplete = (id, completed) => api.post(`/todos/${id}/complete`, completed === undefined ? {} : { completed });
export const deleteTodo = (id) => api.delete(`/todos/${id}`);

// ==================== MEETINGS API ====================
export const getMeetings = (params = {}) => api.get('/meetings', { params });
export const createMeeting = (data) => api.post('/meetings', data);
export const updateMeeting = (id, data) => api.put(`/meetings/${id}`, data);
export const deleteMeeting = (id) => api.delete(`/meetings/${id}`);

export default {
  // Clients
  getClients,
  getClient,
  createClient,
  updateClient,
  deleteClient,
  
  // Jobs
  getJobs,
  getJob,
  createJob,
  updateJob,
  deleteJob,
  
  // Job Comments
  getJobComments,
  addJobComment,
  deleteJobComment,
  setJobFieldConditions,

  getUnclassifiedJobs,
  setJobServiceType,

  // Crews
  getCrews,
  createCrew,
  updateCrew,
  addCrewMember,
  removeCrewMember,
  setCrewMemberRole,
  assignJobCrew,
  
  // Job Team Assignment
  removeSelfFromJob,
  selfAssignToJob,
  
  // Team
  getTeamMembers,
  getTeamMember,
  createTeamMember,
  updateTeamMember,
  deleteTeamMember,
  
  // Time Tracking
  clockIn,
  clockOut,
  clockInCrew,
  clockOutJob,
  pauseTimeEntry,
  resumeTimeEntry,
  getTimeEntries,
  getActiveTimeEntries,
  getJobTimeEntries,
  getWorkOrders,
  splitTimeEntry,
  approveTimeEntry,
  startBreak,
  endBreak,
  
  // Payroll
  getPayrollReport,
  
  // Dashboard
  getDashboardStats,
  
  // Map
  getMapJobs,
  uploadBoundaryFile,
  deleteBoundaryFile,
  
  // Auth
  login,
  logout,
  getCurrentUser,
  register,

  // Parcel Import
  getParcelFolders,
  getParcelProjects,
  previewParcelData,
  importParcelsToJob,
  getJobParcels,
  deleteJobParcel,
  clearJobParcels,
  uploadParcelsJson,

  // Leadership Nominations
  getNominationCategories,
  getNominations,
  getNomination,
  createNomination,
  updateNomination
};